import React, { useState } from "react";
import * as Styled from "./EmbedModal.styled";
import { IMediaResponseDto, Switch } from "../../../libs";
import { useAuth } from "../../../behavioral";
import { embedService, vodService } from "../../../services";
import { toast } from "react-toastify";

interface IProps {
    show: boolean;
    onHide: () => void;
    mediaItem: IMediaResponseDto;
    onUpdateUploads?: () => void;
}

const BASE_SLUG_URL = process.env.REACT_APP_EMBED_URL;

export const EmbedModal: React.FC<IProps> = ({ show, onHide, mediaItem, onUpdateUploads }) => {
    const [slug, setSlug] = useState<string | null>(mediaItem.embedInfo?.slug?.value ?? null);
    const [slugInput, setSlugInput] = useState("");
    const [isEditingSlug, setIsEditingSlug] = useState(false);
    const [copied, setCopied] = useState(false);
    const [slugCopied, setSlugCopied] = useState(false);
    const [iframeCopied, setIframeCopied] = useState(false);
    const authInfo = useAuth();

    const handleCopy = (text: string, setter: React.Dispatch<React.SetStateAction<boolean>>) => {
        navigator.clipboard.writeText(text);
        setter(true);
        setTimeout(() => setter(false), 2000);
    };

    const genericLink = React.useMemo(() => {
        return `${BASE_SLUG_URL}/m/${authInfo?.user?.username}/${mediaItem.mediaId}`;
    }, [mediaItem.mediaId, authInfo?.user?.username]);

    const showSlugInput = !slug || isEditingSlug;
    const slugLink = slug ? `${BASE_SLUG_URL}/s/${slug}` : "";

    const isEmbedEnabled = React.useMemo(() => {
        return mediaItem.embedInfo?.enabled ?? false;
    }, [mediaItem]);

    const isGenericSlugEnabled = React.useMemo(() => {
        return mediaItem.embedInfo?.genericSlugEnabled ?? false;
    }, [mediaItem]);

    const isCustomSlugEnabled = React.useMemo(() => {
        return mediaItem.embedInfo?.slug?.enabled ?? false;
    }, [mediaItem]);

    const isEmbedPlayerEnabled = React.useMemo(() => {
        return mediaItem.embedInfo?.embedPlayerEnabled ?? false;
    }, [mediaItem]);

    const iframeSnippet = React.useMemo(() => {
        return `<iframe src="${genericLink}" width="640" height="360" frameborder="0" allowfullscreen allow="autoplay; encrypted-media"></iframe>`;
    }, [genericLink]);

    React.useEffect(() => {
        setSlug(mediaItem.embedInfo?.slug?.value ?? null);
    }, [mediaItem.embedInfo?.slug?.value]);

    const handleToggleEmbedVideo = React.useCallback(async () => {
        const updatedEmbedInfo = {
            ...(mediaItem.embedInfo || {}),
            enabled: !isEmbedEnabled,
        };
        if (mediaItem.category === "GENERAL") {
            await vodService.editGeneralMediaItem({
                mediaId: mediaItem.mediaId,
                embedInfo: updatedEmbedInfo,
            });
        } else {
            await vodService.editFilmFestivalMediaItem({
                mediaId: mediaItem.mediaId,
                embedInfo: updatedEmbedInfo,
            });
        }
        onUpdateUploads && onUpdateUploads();
    }, [isEmbedEnabled, mediaItem, onUpdateUploads]);

    const handleToggleGenericSlug = React.useCallback(async () => {
        const updatedEmbedInfo = {
            ...(mediaItem.embedInfo || {}),
            genericSlugEnabled: !isGenericSlugEnabled,
        };
        if (mediaItem.category === "GENERAL") {
            await vodService.editGeneralMediaItem({
                mediaId: mediaItem.mediaId,
                embedInfo: updatedEmbedInfo,
            });
        } else {
            await vodService.editFilmFestivalMediaItem({
                mediaId: mediaItem.mediaId,
                embedInfo: updatedEmbedInfo,
            });
        }
        onUpdateUploads && onUpdateUploads();
    }, [isGenericSlugEnabled, mediaItem, onUpdateUploads]);

    const handleSaveSlug = React.useCallback(async () => {
        if (!slugInput.trim()) return;

        try {
            const resp = await embedService.createOrUpdateSlug(mediaItem.mediaId, slugInput.trim());
            if (!resp.success) {
                toast.error(resp.message || "Failed to save slug. Please try a different one.");
                return;
            }
            setSlug(slugInput.trim());
            const updatedEmbedInfo = {
                ...(mediaItem.embedInfo || {}),
                slug: {
                    enabled: true,
                    value: slugInput.trim(),
                },
            };

            if (mediaItem.category === "GENERAL") {
                await vodService.editGeneralMediaItem({
                    mediaId: mediaItem.mediaId,
                    embedInfo: updatedEmbedInfo,
                });
            } else {
                await vodService.editFilmFestivalMediaItem({
                    mediaId: mediaItem.mediaId,
                    embedInfo: updatedEmbedInfo,
                });
            }

            setIsEditingSlug(false);
            onUpdateUploads && onUpdateUploads();
        } catch (error) {
            console.error("Error saving slug:", error);
            toast.error("There was an error saving the slug. Please try again.");
        }
    }, [slugInput, mediaItem, onUpdateUploads]);

    const handleToggleEmbedPlayer = React.useCallback(async () => {
        const updatedEmbedInfo = {
            ...(mediaItem.embedInfo || {}),
            embedPlayerEnabled: !isEmbedPlayerEnabled,
        };
        if (mediaItem.category === "GENERAL") {
            await vodService.editGeneralMediaItem({
                mediaId: mediaItem.mediaId,
                embedInfo: updatedEmbedInfo,
            });
        } else {
            await vodService.editFilmFestivalMediaItem({
                mediaId: mediaItem.mediaId,
                embedInfo: updatedEmbedInfo,
            });
        }
        onUpdateUploads && onUpdateUploads();
    }, [isEmbedPlayerEnabled, mediaItem, onUpdateUploads]);

    const handleCustomSlugToggle = React.useCallback(async () => {

        try {
            const updatedEmbedInfo = {
                ...(mediaItem.embedInfo || {}),
                slug: {
                    enabled: !isCustomSlugEnabled,
                    value: mediaItem.embedInfo?.slug?.value ?? "",
                },
            };

            if (mediaItem.category === "GENERAL") {
                await vodService.editGeneralMediaItem({
                    mediaId: mediaItem.mediaId,
                    embedInfo: updatedEmbedInfo,
                });
            } else {
                await vodService.editFilmFestivalMediaItem({
                    mediaId: mediaItem.mediaId,
                    embedInfo: updatedEmbedInfo,
                });
            }
            onUpdateUploads && onUpdateUploads();
        } catch (error) {
            console.error("Error toggling custom slug:", error);
            toast.error("There was an error toggling the custom slug. Please try again.");
        }

    }, [isCustomSlugEnabled, mediaItem, onUpdateUploads]);


    return (
        <Styled.EmbedModalContainer show={show} onHide={onHide} centered dialogClassName="modal-width">
            <Styled.EmbedModalHeader closeButton>
                <Styled.EmbedModalContainer.Title>
                    Embed Video
                </Styled.EmbedModalContainer.Title>
            </Styled.EmbedModalHeader>
            <Styled.EmbedModalContainer.Body>
                <Styled.HolderContainer>
                    <Styled.HolderHeader>
                        <Styled.HolderTitle>
                            Enable Embed Video
                        </Styled.HolderTitle>
                        <Switch
                            onChange={handleToggleEmbedVideo}
                            checked={isEmbedEnabled}
                        />
                    </Styled.HolderHeader>
                    <Styled.Spacer />

                    {/* Get Generic Link */}
                    <Styled.HolderContainer collapsed={!isEmbedEnabled}>
                        <Styled.HolderContainer>
                            <Styled.HolderHeader>
                                <Styled.HolderTitle>
                                    Get Generic Link
                                </Styled.HolderTitle>
                                <Switch
                                    onChange={handleToggleGenericSlug}
                                    checked={isGenericSlugEnabled}
                                />
                            </Styled.HolderHeader>
                            <Styled.CollapsibleContent collapsed={!isGenericSlugEnabled}>
                                <Styled.Spacer />
                                <Styled.LinkRow>
                                    <Styled.LinkInput
                                        type="text"
                                        value={genericLink}
                                        disabled
                                        readOnly
                                    />
                                    <Styled.CopyButton onClick={() => handleCopy(genericLink, setCopied)}>
                                        {copied ? "Copied!" : "Copy"}
                                    </Styled.CopyButton>
                                </Styled.LinkRow>
                            </Styled.CollapsibleContent>
                        </Styled.HolderContainer>

                        <Styled.Spacer />

                        {/* Embed Player */}
                        <Styled.HolderContainer>
                            <Styled.HolderHeader>
                                <Styled.HolderTitle>
                                    Enable Embed Player
                                </Styled.HolderTitle>
                                <Switch
                                    onChange={handleToggleEmbedPlayer}
                                    checked={isEmbedPlayerEnabled}
                                />
                            </Styled.HolderHeader>
                            <Styled.CollapsibleContent collapsed={!isEmbedPlayerEnabled}>
                                <Styled.Spacer />
                                <Styled.LinkRow>
                                    <Styled.IframeTextArea
                                        value={iframeSnippet}
                                        disabled
                                        readOnly
                                        rows={3}
                                    />
                                    <Styled.CopyButton onClick={() => handleCopy(iframeSnippet, setIframeCopied)}>
                                        {iframeCopied ? "Copied!" : "Copy"}
                                    </Styled.CopyButton>
                                </Styled.LinkRow>
                            </Styled.CollapsibleContent>
                        </Styled.HolderContainer>

                        <Styled.Spacer />

                        {/* Create Custom Slug Link */}
                        <Styled.HolderContainer>
                            <Styled.HolderHeader>
                                <Styled.HolderTitle>
                                    Create Custom Slug Link
                                </Styled.HolderTitle>
                                <Switch
                                    onChange={handleCustomSlugToggle}
                                    checked={isCustomSlugEnabled}
                                />
                            </Styled.HolderHeader>
                            <Styled.CollapsibleContent collapsed={!isCustomSlugEnabled}>
                                <Styled.Spacer />
                                {showSlugInput ? (
                                    <>
                                        <Styled.SlugInputRow>
                                            <Styled.TextInput
                                                type="text"
                                                placeholder="Enter custom slug..."
                                                value={slugInput}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSlugInput(e.target.value)}
                                            />
                                        </Styled.SlugInputRow>
                                        <Styled.Spacer />
                                        <Styled.SlugActionRow>
                                            <Styled.SaveSlugButton onClick={handleSaveSlug}>
                                                Check &amp; Save Slug
                                            </Styled.SaveSlugButton>
                                            {isEditingSlug && (
                                                <Styled.CancelButton onClick={() => setIsEditingSlug(false)}>
                                                    Cancel
                                                </Styled.CancelButton>
                                            )}
                                        </Styled.SlugActionRow>
                                    </>
                                ) : (
                                    <Styled.SlugDisplayRow>
                                        <Styled.SlugText>{slug}</Styled.SlugText>
                                        <Styled.EditButton onClick={() => { setSlugInput(slug!); setIsEditingSlug(true); }}>
                                            Edit
                                        </Styled.EditButton>
                                    </Styled.SlugDisplayRow>
                                )}

                                {slug && !isEditingSlug && (
                                    <>
                                        <Styled.Spacer />
                                        <Styled.LinkRow>
                                            <Styled.LinkInput
                                                type="text"
                                                value={slugLink}
                                                disabled
                                                readOnly
                                            />
                                            <Styled.CopyButton onClick={() => handleCopy(slugLink, setSlugCopied)}>
                                                {slugCopied ? "Copied!" : "Copy"}
                                            </Styled.CopyButton>
                                        </Styled.LinkRow>
                                    </>
                                )}
                            </Styled.CollapsibleContent>
                        </Styled.HolderContainer>
                    </Styled.HolderContainer>
                </Styled.HolderContainer>
            </Styled.EmbedModalContainer.Body>
        </Styled.EmbedModalContainer>
    );
}
